import {
  default as Button,
  default as CustomButton,
} from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import WorkflowStatus from "@/components/WorkflowStatus";
import { capitalizeText } from "@/lib/capitalizeText";
import { checkFormChanges } from "@/lib/checkFormChanges";
import {
  mergeClearedSchemaFieldsForUpdate,
  normalizeMissingSchemaKeys,
  prepareInitialValues,
  prepareSubmitValues,
} from "@/lib/dynamicSchemaFormValues";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { TenantSchema, TenantSchemaProperty } from "@/utils/types/schemas";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "antd";
import { Printer, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/* -------------------- Helpers -------------------- */

const hasSchema = (
  json: TenantSchema["json"] | {}
): json is TenantSchemaProperty | Record<string, any> => {
  return (
    typeof json !== "string" &&
    json &&
    typeof json === "object" &&
    "schema" in json
  );
};

const hasXComponent = (property: any): property is any => {
  return property && typeof property === "object" && "x-component" in property;
};

/* -------------------- Component -------------------- */

const ModuleFormPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { schemaName = "", id } = useParams<{
    schemaName: string;
    id: string;
  }>();

  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useMemo(() => createForm({}), []);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName),
    enabled: !!schemaName,
  });

  const { data: moduleInfo, isLoading: moduleLoading } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: () => DynamicModuleService.getItemById(schemaName, id ?? ""),
    enabled: isEditMode && !!schemaName && !!id,
  });

  const handleSubmit = async () => {
    await form.validate();

    let modifiedValues = removeMetaFields(form.values);

    // ✅ Convert values (dayjs → ISO, undefined → null)
    modifiedValues = prepareSubmitValues(modifiedValues);

    setIsSubmitting(true);

    if (schemaInfo?.json && hasSchema(schemaInfo.json)) {
      const properties = schemaInfo.json.schema?.properties ?? {};
      Object.entries(properties).forEach(([key, property]: [string, any]) => {
        if (hasXComponent(property)) {
          const component = property["x-component"];
          const value = modifiedValues[key];

          if (component === "CustomUpload") {
            if (Array.isArray(value) && value.length > 0 && value[0]?.id) {
              modifiedValues[key] = value[0].id;
            }
          } else if (component === "CustomImageUpload") {
            if (value && typeof value === "object" && "id" in value) {
              modifiedValues[key] = value.id;
            }
          }
        }
      });

      // Cleared fields may be omitted from form.values; send explicit nulls on update.
      if (isEditMode && moduleInfo) {
        const cleanedRecord = removeMetaFields(moduleInfo);
        modifiedValues = mergeClearedSchemaFieldsForUpdate(
          modifiedValues,
          cleanedRecord,
          properties
        );
      }

      // Normalize for comparison
      if (isEditMode && moduleInfo) {
        const originalData = prepareSubmitValues(removeMetaFields(moduleInfo));

        const normalizedModified = normalizeMissingSchemaKeys(
          modifiedValues,
          properties
        );
        const normalizedOriginal = normalizeMissingSchemaKeys(
          originalData,
          properties
        );

        if (!checkFormChanges(normalizedModified, normalizedOriginal, notify)) {
          setIsSubmitting(false);
          return;
        }
      }
    }

    /* -------------------- API -------------------- */

    if (isEditMode) {
      const response = await DynamicModuleService.updateItem(
        schemaName,
        id!,
        modifiedValues
      );
      if (response) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["dynamic-model", schemaName, id],
          }),
          queryClient.invalidateQueries({
            queryKey: ["moduleData", schemaName],
          }),
        ]);
        navigate(navigationRoutes.MODULE(schemaName ?? ""));
      }
    } else {
      const response = await DynamicModuleService.createItem(
        schemaName,
        modifiedValues
      );
      if (response) {
        await queryClient.invalidateQueries({
          queryKey: ["moduleData", schemaName],
        });
        navigate(navigationRoutes.MODULE(schemaName ?? ""));
      }
    }

    setIsSubmitting(false);
  };

  /* -------------------- Load Initial Values -------------------- */

  useEffect(() => {
    if (isEditMode && moduleInfo) {
      const cleaned = removeMetaFields(moduleInfo);
      const prepared = prepareInitialValues(cleaned);

      form.setInitialValues(prepared);
      form.reset();
    }
  }, [isEditMode, moduleInfo, form]);

  /* -------------------- UI -------------------- */

  if (isEditMode && moduleLoading) {
    return <LoadingSpinner />;
  }
  if (isEditMode && !moduleInfo) {
    return <NotFound entityName="Module" />;
  }
  if (schemaLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            <div className="flex w-full items-center justify-between gap-2.5">
              <span className="text-lg font-semibold">
                {isEditMode
                  ? `Edit ${capitalizeText(schemaName)}`
                  : `Add ${capitalizeText(schemaName)}`}
              </span>

              {isEditMode && (
                <WorkflowStatus schemaName={schemaName} recordId={id ?? ""} />
              )}
            </div>
          }
          extra={
            <div className="flex items-center gap-3">
              {isEditMode && (
                <Button
                  type="primary"
                  onClick={() =>
                    navigate(navigationRoutes.PRINT(schemaName ?? "", id ?? ""))
                  }
                >
                  <Printer size={16} />
                </Button>
              )}

              <CustomButton
                type="primary"
                icon={<Save size={16} />}
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditMode
                    ? "Save Changes"
                    : "Save"}
              </CustomButton>
            </div>
          }
          onBack={() => window.history.back()}
        />
      }
    >
      <SchemaFormView form={form} schemaJson={schemaInfo?.json ?? schemaInfo} />
    </Card>
  );
};

export default ModuleFormPage;
